import { 
    Box, 
    Button, 
    VStack, 
    HStack,   
    Textarea,
    IconButton,
    Icon,
    Text,
    Menu,
    MenuItem,
    MenuList,
    MenuButton,
    Flex,
} from "@chakra-ui/react";
import { useChat } from "context/ChatBotContext";
import { useEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import ChatMessage from "components/chat/ChatMessage";
import { AddIcon } from "@chakra-ui/icons";
import { TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";

interface ChatBotProps {
  onToggleChat: () => void;
}

const ChatBot: React.FC<ChatBotProps> = ({ onToggleChat }) => {
  const {messages, handleNewChat, isLoading, handleContinueTisaChat } = useChat();
  const height = 20; // Height state for textarea
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [inputMessage, setInputMessage] = useState("");
  const [userMessages, setUserMessages] = useState<string[]>([]);

  const scrollToBottom = () =>{
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  };

  useEffect (()=>{
    scrollToBottom();
  }, [messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>{
      setInputMessage(e.target.value);
      setUserMessages([ e.target.value]);

      e.target.style.height="auto";
      e.target.style.height=`${e.target.scrollHeight}px`;
  };

  const handleSendMessage = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (userMessages.length > 0 && userMessages[0].trim() !== "") {
      const messagesToSend = [...userMessages]; 
      // Clear inputs immediately
      setInputMessage("");
      setUserMessages([]);

      await handleContinueTisaChat(messagesToSend);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };
  
  return (
    <Box
      maxH="93vh"
      h="100%"
      p={2}
      borderRadius="lg"
      boxShadow="lg"
      bg="bg.surface"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      zIndex={27}
    >
      <Flex
        position="relative"
        justifyContent="center" 
        alignItems="center" 
        py={2}
        px={3}
        bg="bg.surface"
        borderRadius="md"
        mb={2}
      >
        <Menu>
          <MenuButton 
            as={Button} 
            variant="ghost"
            px={3}
            py={0}
            bg="bg.surface"
            borderRadius="md"
            role="group"
            _hover={{bg: "purple.50"}}
            size="sm"
          >
            <Box display="flex" alignItems="center" minH="24px">
              <Text fontSize="md" color="purple.700" fontWeight={600} mb={0} mr={2} fontFamily="Montserrat">
                Anko
              </Text>
              <Icon as={IoIosArrowDown} color="purple.700" mb={0} mr={0} boxSize={4}></Icon> 
            </Box>
          </MenuButton>
          <MenuList bg="red" m={0} p={0}>
            <MenuItem 
              icon={<AddIcon />} 
              onClick={handleNewChat} 
              fontSize="sm" 
              fontWeight={600}
              _hover={{bg: "purple.50"}}
              whiteSpace="normal"
              wordBreak="break-word"
            >
              New Chat
            </MenuItem>
          </MenuList>
        </Menu>
        <IconButton
          icon={<Icon as={TbLayoutSidebarRightCollapseFilled} boxSize={7} />}
          aria-label="Close chat"
          position="absolute"
          right={2}
          top={2}
          size="sm"
          variant="ghost"
          onClick={onToggleChat}
          color="gray.400"
          _hover={{ bg: "gray.200", color: "gray.700" }}
        />
      </Flex>
      <VStack 
        spacing={4} 
        align="stretch" 
        overflowY="auto" 
        flex={1}
        pt={7}
        pb={7}
        px={1}
        position="relative"
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        {messages.length === 0 ? (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="90%"
            maxW="400px"
          >
            <Button
              width="100%"
              height="auto"
              display="flex"
              flexDirection="column"
              bg="bg.surface"
              px="6"
              py="7"
              cursor="default"
            >
            <Text color="gray.700" fontSize="lg" fontWeight="medium" mb={0}>
              How can I help?
            </Text>
            </Button>
          </Box>
        ) : (
          messages.map((msg, index) => (
            <ChatMessage
              key={index}
              text={msg.text}
              sender={msg.sender}
              isLoading={isLoading && index === messages.length - 1 && msg.sender === "bot"}
              isLastMessage={index === messages.length - 1}
              toolCalls={msg.toolCalls}
              execution_id={msg.execution_id}
            />
          ))
        )}
        {isLoading && messages[messages.length - 1]?.sender === "user" && (
          <ChatMessage
            text=""
            sender="bot"
            isLoading={true}
            isLastMessage={true}
            toolCalls={[]}
          />
        )}
        <div ref={messagesEndRef} />
      </VStack>
        
        <HStack>
          <Textarea
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Ask me anything on your data"
            resize="none"
            minHeight={height}
            maxHeight={100}
            flex={1}
            isDisabled={isLoading} 
            borderColor="gray.100"
            focusBorderColor="purple.100"
            fontSize="sm"
          />
          <IconButton 
            icon={<Icon as={FaArrowUp} boxSize={4} color="purple.700" />}
            onClick={handleSendMessage}
            colorScheme="purple"
            bg="gray.100"
            isDisabled={isLoading} 
            aria-label={"Send Chat Message"} 
            borderRadius="full"
            size="sm"
            _hover={{"bg": "purple.100", "color":"gray.700"}}
          />
        </HStack>
      </Box>
  );
};
  
export default ChatBot;